import { supabase } from '../lib/supabase';

export const negotiationService = {
  /**
   * Start a new negotiation for a marketplace_wishes offer
   */
  async startMarketplaceNegotiation(offerId, offerData, initialMessage) {
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('Niste prijavljeni');

      const user = session?.user;

      if (offerData?.userId === user?.id) {
        throw new Error('Ne možete pregovarati sa samim sobom');
      }

      // All offers now come from marketplace_wishes — always use offer_id
      const negotiationPayload = {
        buyer_id: user?.id,
        seller_id: offerData?.userId,
        initiator_id: user?.id,
        responder_id: offerData?.userId,
        status: 'initiated',
        initial_message: initialMessage || 'Pregovaranje pokrenuto',
        offer_id: offerId,
      };

      // Create negotiation record
      const { data, error } = await supabase
        ?.from('negotiations')
        ?.insert(negotiationPayload)
        ?.select('id, status, created_at, offer_id, buyer_id, seller_id')
        ?.single();

      if (error) throw error;

      // Send initial message if provided
      if (initialMessage && data?.id) {
        await supabase?.from('negotiation_messages')?.insert({
          negotiation_id: data?.id,
          sender_id: user?.id,
          message_type: 'text',
          message_content: initialMessage,
        });
      }

      return data;
    } catch (error) {
      console.error('Error starting marketplace negotiation:', error);
      throw error;
    }
  },

  /**
   * Get all marketplace negotiations for current user
   */
  async getMarketplaceNegotiations() {
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('Niste prijavljeni');

      const user = session?.user;

      const { data, error } = await supabase
        ?.from('negotiations')
        ?.select(`
          id,
          status,
          initial_message,
          created_at,
          updated_at,
          buyer_id,
          seller_id,
          initiator_id,
          responder_id,
          offer_id,
          close_requested_by,
          offer:marketplace_wishes!offer_id(
            id,
            wish_title,
            voucher_name,
            wish_type,
            max_price,
            currency,
            token_quantity
          ),
          buyer:user_profiles!buyer_id(id, full_name, email, profile_picture_url),
          seller:user_profiles!seller_id(id, full_name, email, profile_picture_url)
        `)
        ?.or(`buyer_id.eq.${user?.id},seller_id.eq.${user?.id},initiator_id.eq.${user?.id},responder_id.eq.${user?.id}`)
        ?.order('updated_at', { ascending: false });

      if (error) throw error;

      return (data || [])?.map(n => ({
        id: n?.id,
        status: n?.status,
        initialMessage: n?.initial_message,
        createdAt: n?.created_at,
        updatedAt: n?.updated_at,
        offerId: n?.offer_id,
        buyerId: n?.buyer_id,
        sellerId: n?.seller_id,
        initiatorId: n?.initiator_id,
        responderId: n?.responder_id,
        offerName: n?.offer?.wish_title || n?.offer?.voucher_name || 'Oglas',
        offerType: n?.offer?.wish_type,
        maxPrice: n?.offer?.max_price,
        currency: n?.offer?.currency,
        tokenQuantity: n?.offer?.token_quantity,
        counterpartyName: (n?.buyer_id === user?.id)
          ? (n?.seller?.full_name || 'Prodavac')
          : (n?.buyer?.full_name || 'Kupac'),
        counterpartyId: (n?.buyer_id === user?.id) ? n?.seller_id : n?.buyer_id,
        isMyOffer: n?.seller_id === user?.id,
        closeRequestedBy: n?.close_requested_by,
      }));
    } catch (error) {
      console.error('Error fetching marketplace negotiations:', error);
      throw error;
    }
  },

  /**
   * Get messages for a negotiation
   */
  async getMessages(negotiationId) {
    try {
      const { data, error } = await supabase
        ?.from('negotiation_messages')
        ?.select(`
          id,
          negotiation_id,
          sender_id,
          message_type,
          message_content,
          offer_details,
          is_read,
          created_at,
          sender:user_profiles!sender_id(id, full_name, profile_picture_url)
        `)
        ?.eq('negotiation_id', negotiationId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return (data || [])?.map(m => ({
        id: m?.id,
        negotiationId: m?.negotiation_id,
        senderId: m?.sender_id,
        messageType: m?.message_type,
        messageContent: m?.message_content,
        offerDetails: m?.offer_details,
        isRead: m?.is_read,
        createdAt: m?.created_at,
        senderName: m?.sender?.full_name || 'Korisnik',
        senderAvatar: m?.sender?.profile_picture_url,
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  /**
   * Send a text message
   */
  async sendMessage(negotiationId, messageContent, messageType = 'text', offerDetails = null) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Niste prijavljeni');

      const { data, error } = await supabase
        ?.from('negotiation_messages')
        ?.insert({
          negotiation_id: negotiationId,
          sender_id: session?.user?.id,
          message_type: messageType,
          message_content: messageContent,
          offer_details: offerDetails,
        })
        ?.select(`
          id, negotiation_id, sender_id, message_type, message_content,
          offer_details, is_read, created_at,
          sender:user_profiles!sender_id(id, full_name, profile_picture_url)
        `)
        ?.single();

      if (error) throw error;

      // Update negotiation updated_at and status to in_progress
      await supabase?.from('negotiations')?.update({
        updated_at: new Date()?.toISOString(),
        status: 'in_progress',
      })?.eq('id', negotiationId);

      return {
        id: data?.id,
        negotiationId: data?.negotiation_id,
        senderId: data?.sender_id,
        messageType: data?.message_type,
        messageContent: data?.message_content,
        offerDetails: data?.offer_details,
        isRead: data?.is_read,
        createdAt: data?.created_at,
        senderName: data?.sender?.full_name || 'Korisnik',
        senderAvatar: data?.sender?.profile_picture_url,
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * Update negotiation status
   */
  async updateStatus(negotiationId, status, agreedTerms = null) {
    try {
      const updates = {
        status,
        updated_at: new Date()?.toISOString(),
      };
      if (agreedTerms) updates.agreed_terms = agreedTerms;
      if (status === 'completed' || status === 'agreed') {
        updates.completed_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase
        ?.from('negotiations')
        ?.update(updates)
        ?.eq('id', negotiationId)
        ?.select('id, status, updated_at')
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating negotiation status:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time messages for a negotiation
   */
  subscribeToMessages(negotiationId, onMessage) {
    const channel = supabase
      ?.channel(`neg-messages-${negotiationId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiation_messages',
          filter: `negotiation_id=eq.${negotiationId}`,
        },
        (payload) => onMessage(payload?.new)
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to negotiation status changes
   */
  subscribeToNegotiationStatus(negotiationId, onUpdate) {
    const channel = supabase
      ?.channel(`neg-status-${negotiationId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiations',
          filter: `id=eq.${negotiationId}`,
        },
        (payload) => onUpdate(payload?.new)
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Mark messages as read
   */
  async markMessagesAsRead(negotiationId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return;

      await supabase
        ?.from('negotiation_messages')
        ?.update({ is_read: true, read_at: new Date()?.toISOString() })
        ?.eq('negotiation_id', negotiationId)
        ?.neq('sender_id', session?.user?.id)
        ?.eq('is_read', false);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  },

  // ---- Legacy methods (kept for backward compatibility) ----

  async startNegotiation(wishId, initialMessage, initialOffer = null) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const user = session?.user;

      const { data: wish, error: wishError } = await supabase
        ?.from('voucher_wishes')?.select('user_id')?.eq('id', wishId)?.single();
      if (wishError) throw wishError;
      if (wish?.user_id === user?.id) throw new Error('Cannot start negotiation with yourself');

      const { data, error } = await supabase
        ?.from('negotiations')
        ?.insert({
          wish_id: wishId,
          initiator_id: user?.id,
          responder_id: wish?.user_id,
          initial_message: initialMessage,
          current_offer: initialOffer,
        })
        ?.select('*')?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error starting negotiation:', error);
      throw error;
    }
  },

  async getUserNegotiations(status = null) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const user = session?.user;

      let query = supabase
        ?.from('negotiations')
        ?.select('*')
        ?.or(`initiator_id.eq.${user?.id},responder_id.eq.${user?.id}`);

      if (status) query = query?.eq('status', status);
      query = query?.order('updated_at', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching negotiations:', error);
      throw error;
    }
  },

  async getNegotiationDetails(negotiationId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('negotiations')
        ?.select(`
          *,
          initiator:user_profiles!initiator_id(id, full_name, email, rating, profile_picture_url),
          responder:user_profiles!responder_id(id, full_name, email, rating, profile_picture_url)
        `)
        ?.eq('id', negotiationId)
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching negotiation details:', error);
      throw error;
    }
  },

  async updateNegotiationStatus(negotiationId, status, agreedTerms = null) {
    return this.updateStatus(negotiationId, status, agreedTerms);
  },

  async sendMessageWithNotification(negotiationId, messageContent, messageType = 'text', offerDetails = null) {
    return this.sendMessage(negotiationId, messageContent, messageType, offerDetails);
  },

  async updateNegotiationStatusWithNotification(negotiationId, status, agreedTerms = null) {
    return this.updateStatus(negotiationId, status, agreedTerms);
  },

  async getUnreadCount() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return 0;
      const user = session?.user;

      const { data: negotiations } = await supabase
        ?.from('negotiations')
        ?.select('id')
        ?.or(`initiator_id.eq.${user?.id},responder_id.eq.${user?.id},buyer_id.eq.${user?.id},seller_id.eq.${user?.id}`);

      const ids = negotiations?.map(n => n?.id) || [];
      if (ids?.length === 0) return 0;

      const { count } = await supabase
        ?.from('negotiation_messages')
        ?.select('*', { count: 'exact', head: true })
        ?.in('negotiation_id', ids)
        ?.neq('sender_id', user?.id)
        ?.eq('is_read', false);

      return count || 0;
    } catch (error) {
      return 0;
    }
  },

  subscribeToNegotiation(negotiationId, callback) {
    const channel = supabase
      ?.channel(`negotiation:${negotiationId}`)
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'negotiation_messages', filter: `negotiation_id=eq.${negotiationId}` },
        (payload) => callback('message', payload?.new))
      ?.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'negotiations', filter: `id=eq.${negotiationId}` },
        (payload) => callback('status', payload?.new))
      ?.subscribe();
    return channel;
  },

  formatNegotiation(data) {
    return {
      id: data?.id,
      wishId: data?.wish_id,
      initiatorId: data?.initiator_id,
      responderId: data?.responder_id,
      status: data?.status,
      initialMessage: data?.initial_message,
      currentOffer: data?.current_offer,
      agreedTerms: data?.agreed_terms,
      transactionId: data?.transaction_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      completedAt: data?.completed_at,
      initiator: data?.initiator ? {
        id: data?.initiator?.id,
        fullName: data?.initiator?.full_name,
        email: data?.initiator?.email,
        rating: data?.initiator?.rating,
        profilePictureUrl: data?.initiator?.profile_picture_url
      } : null,
      responder: data?.responder ? {
        id: data?.responder?.id,
        fullName: data?.responder?.full_name,
        email: data?.responder?.email,
        rating: data?.responder?.rating,
        profilePictureUrl: data?.responder?.profile_picture_url
      } : null,
    };
  },

  formatMessage(data) {
    return {
      id: data?.id,
      negotiationId: data?.negotiation_id,
      senderId: data?.sender_id,
      messageType: data?.message_type,
      messageContent: data?.message_content,
      offerDetails: data?.offer_details,
      isRead: data?.is_read,
      createdAt: data?.created_at,
      sender: data?.sender ? {
        id: data?.sender?.id,
        fullName: data?.sender?.full_name,
        profilePictureUrl: data?.sender?.profile_picture_url
      } : null
    };
  }
};